import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';

export interface StoredBiometricCredentials {
  cardNumber: string;
  mobileNumber: string;
}

@Injectable({ providedIn: 'root' })
export class BiometricAuthService {
  private readonly storageKey = 'auth.biometric.credentials';
  private readonly biometricEnabledKey = 'auth.biometric.enabled';
  private readonly autoPromptRequestKey = 'auth.biometric.autoPromptRequested';

  isBiometricLoginEnabled(): boolean {
    return localStorage.getItem(this.biometricEnabledKey) !== '0';
  }

  setBiometricLoginEnabled(enabled: boolean): void {
    localStorage.setItem(this.biometricEnabledKey, enabled ? '1' : '0');
    if (!enabled) {
      sessionStorage.removeItem(this.autoPromptRequestKey);
    }
  }

  async isAvailable(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    const biometry = await BiometricAuth.checkBiometry();
    return biometry.isAvailable;
  }

  hasStoredCredentials(): boolean {
    return this.getStoredCredentials() !== null;
  }

  getStoredCredentials(): StoredBiometricCredentials | null {
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<StoredBiometricCredentials>;
      const cardNumber = `${parsed.cardNumber ?? ''}`.replace(/\D/g, '');
      const mobileNumber = `${parsed.mobileNumber ?? ''}`.replace(/\D/g, '');

      if (!cardNumber || !mobileNumber) {
        return null;
      }

      return { cardNumber, mobileNumber };
    } catch {
      return null;
    }
  }

  setStoredCredentials(credentials: StoredBiometricCredentials): void {
    localStorage.setItem(this.storageKey, JSON.stringify(credentials));
  }

  clearStoredCredentials(): void {
    localStorage.removeItem(this.storageKey);
  }

  requestAutoPromptOnNextCardPage(): void {
    if (!this.isBiometricLoginEnabled()) {
      return;
    }
    sessionStorage.setItem(this.autoPromptRequestKey, '1');
  }

  consumeAutoPromptRequest(): boolean {
    const requested = sessionStorage.getItem(this.autoPromptRequestKey) === '1';
    if (requested) {
      sessionStorage.removeItem(this.autoPromptRequestKey);
    }
    return requested;
  }

  async authenticate(
    reason = 'Sign in to your rewards account'
  ): Promise<StoredBiometricCredentials | null> {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }

    const stored = this.getStoredCredentials();
    if (!stored) {
      return null;
    }

    const available = await this.isAvailable();
    if (!available) {
      return null;
    }

    await BiometricAuth.authenticate({
      reason,
      allowDeviceCredential: true,
      androidConfirmationRequired: false,
    });

    return stored;
  }
}
